// Services list (§11 `/services`). Each service is a target host with signing keys, maintenance
// windows, flow-control caps, and quotas. Click through to /services/:id for detail + edit.
// `?new=1` opens an inline create form (POST /services upsert).

import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Api, ApiError } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { Badge, Card, Empty, ErrorBox, Field, Spinner } from "../components/ui";

export function ServicesPage() {
  const [params, setParams] = useSearchParams();
  const showNew = params.get("new") === "1";
  const { data, error, loading, reload } = useAsync(() => Api.listServices(), []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">Services</h1>
        {showNew ? (
          <button className="btn" onClick={() => setParams({})}>
            Cancel
          </button>
        ) : (
          <button className="btn btn-primary" onClick={() => setParams({ new: "1" })}>
            New service
          </button>
        )}
      </div>

      {showNew && (
        <NewServiceForm
          onCancel={() => setParams({})}
          onCreated={() => {
            setParams({});
            reload();
          }}
        />
      )}

      {loading && !data ? (
        <Spinner />
      ) : error ? (
        <ErrorBox error={error} />
      ) : !data || data.data.length === 0 ? (
        <Empty>No services yet.</Empty>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full bg-surface">
            <thead>
              <tr>
                <th className="th">ID</th>
                <th className="th">Name</th>
                <th className="th">Base URL</th>
                <th className="th">Signing</th>
                <th className="th">Caps</th>
                <th className="th">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((s) => (
                <tr key={s.id} className="hover:bg-bg/50">
                  <td className="td">
                    <Link className="mono text-primary hover:underline" to={`/services/${s.id}`}>
                      {s.id}
                    </Link>
                  </td>
                  <td className="td">{s.name}</td>
                  <td className="td mono text-muted">{s.baseUrl}</td>
                  <td className="td">
                    <Badge tone={s.signingMode === "required" ? "ok" : "muted"}>{s.signingMode}</Badge>
                  </td>
                  <td className="td mono text-xs text-muted">
                    {s.maxRps != null ? `${s.maxRps} rps` : "—"} /{" "}
                    {s.maxParallelism != null ? `${s.maxParallelism} par` : "—"}
                  </td>
                  <td className="td">
                    {s.enabled ? <Badge tone="ok">enabled</Badge> : <Badge tone="danger">disabled</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewServiceForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const navigate = useNavigate();
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("https://");
  const [signingMode, setSigningMode] = useState<"required" | "none">("none");
  const [defaultTimeoutMs, setDefaultTimeoutMs] = useState("30000");
  const [maxRps, setMaxRps] = useState("");
  const [maxParallelism, setMaxParallelism] = useState("");
  const [retainDays, setRetainDays] = useState("90");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<Error | null>(null);

  function num(v: string): number | null {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);

    if (!id.trim()) return setErr(new Error("ID is required"));
    if (!name.trim()) return setErr(new Error("Name is required"));
    if (!baseUrl.startsWith("https://") || baseUrl.length < 9) {
      return setErr(new Error("Base URL must be an absolute https:// URL"));
    }

    const body: Record<string, unknown> = {
      id: id.trim(),
      name: name.trim(),
      baseUrl: baseUrl.trim(),
      signingMode,
      defaultTimeoutMs: num(defaultTimeoutMs) ?? 30000,
      retainDays: num(retainDays) ?? 90,
    };
    const rps = num(maxRps);
    if (rps != null) body.maxRps = rps;
    const par = num(maxParallelism);
    if (par != null) body.maxParallelism = par;

    setSubmitting(true);
    try {
      const created = await Api.upsertService(body);
      onCreated();
      navigate(`/services/${created.id}`);
    } catch (e2) {
      const msg =
        e2 instanceof ApiError
          ? `${e2.message}${e2.details ? ` — ${JSON.stringify(e2.details)}` : ""}`
          : e2 instanceof Error
            ? e2.message
            : "Failed to create service";
      setErr(new Error(msg));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card title="New service">
      <form onSubmit={submit} className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
        <Field label="ID" hint="Stable human id (used in URLs + config). e.g. billing-api">
          <input className="input" value={id} onChange={(e) => setId(e.target.value)} placeholder="billing-api" autoFocus />
        </Field>
        <Field label="Name">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Billing API" />
        </Field>
        <Field label="Base URL" hint="Absolute https:// origin. Task paths are appended to this.">
          <input className="input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.example.com" />
        </Field>
        <Field label="Signing mode" hint="'required' needs Secrets Store signing keys configured.">
          <select className="input" value={signingMode} onChange={(e) => setSigningMode(e.target.value as "required" | "none")}>
            <option value="none">none</option>
            <option value="required">required</option>
          </select>
        </Field>
        <Field label="Default timeout (ms)">
          <input className="input" value={defaultTimeoutMs} onChange={(e) => setDefaultTimeoutMs(e.target.value)} inputMode="numeric" />
        </Field>
        <Field label="Retain days">
          <input className="input" value={retainDays} onChange={(e) => setRetainDays(e.target.value)} inputMode="numeric" />
        </Field>
        <Field label="Max RPS" hint="Optional flow-control cap. Blank = unlimited.">
          <input className="input" value={maxRps} onChange={(e) => setMaxRps(e.target.value)} inputMode="numeric" placeholder="—" />
        </Field>
        <Field label="Max parallelism" hint="Optional in-flight cap. Blank = unlimited.">
          <input className="input" value={maxParallelism} onChange={(e) => setMaxParallelism(e.target.value)} inputMode="numeric" placeholder="—" />
        </Field>

        {err && (
          <div className="md:col-span-2 mb-3">
            <ErrorBox error={err} />
          </div>
        )}

        <div className="md:col-span-2 flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Creating…" : "Create service"}
          </button>
          <button type="button" className="btn" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}